import React, { FC, useEffect, useRef, useState } from 'react';
import { FieldInputProps } from 'formik';
import s from './index.module.scss';
import cx from 'classnames';
import { formatIntegerForInput, parseIntegerInput } from 'core/functions/formatNumber';

type InputTextProps = {
	placeholder?: string;
	isDisabled?: boolean;
	field: FieldInputProps<string>;
	id?: string;
	setFieldValue: (field: string, value: string) => void;
	onBlur?: () => void;
	fieldValue?: string;
	isTrimValue?: boolean;
	parentClassName?: string;
	type?: 'text' | 'number';
	/** Use text input with thousands separators; form value stays digits-only. */
	formatIntegerDisplay?: boolean;
	isMultiple?: boolean;
	isDonation?: boolean;
	dir?: 'ltr' | 'rtl';
};

let timeout: NodeJS.Timeout;

const InputText: FC<InputTextProps> = ({
	placeholder,
	isDisabled,
	field,
	id,
	setFieldValue,
	fieldValue,
	isTrimValue,
	parentClassName,
	type = 'text',
	formatIntegerDisplay = false,
	isMultiple,
	isDonation,
	onBlur,
	dir,
}: InputTextProps) => {
	const ref = useRef<HTMLInputElement>(null);
	const [isFocused, setIsFocused] = useState(false);

	const setFocus = (): void => {
		if (ref && ref.current) {
			ref.current.focus();
		}
	};

	const resolvedValue = isMultiple ? fieldValue : fieldValue ?? field.value;
	const rawIntegerValue = parseIntegerInput(String(resolvedValue ?? ''));

	const syncIntegerValue = (digits: string) => {
		setFieldValue(field.name, digits);
		field.onChange({
			target: { name: field.name, value: digits },
		} as React.ChangeEvent<HTMLInputElement>);
	};

	useEffect(() => {
		if (!!fieldValue && !!field && !!setFieldValue && !isMultiple && !isDonation && !formatIntegerDisplay) {
			window.clearTimeout(timeout);
			timeout = setTimeout(() => {
				setFieldValue(field?.name, fieldValue.trim());
			}, 700);
		}

		if (isDonation) setFieldValue(field.name, String(fieldValue || '').trim());

		if (isMultiple && fieldValue) setFieldValue(field.name, String(fieldValue || '').trim());
	}, [fieldValue, setFieldValue, isMultiple, isDonation, formatIntegerDisplay, field.name]);

	useEffect(() => {
		if (isTrimValue) setFieldValue(field.name, String(field.value)?.trim());
	}, [field.value, isTrimValue, field.name, setFieldValue]);

	useEffect(() => {
		if (type === 'number' && !formatIntegerDisplay) {
			ref.current?.addEventListener('keypress', e => {
				if (!['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) e.preventDefault();
			});
		}
	}, [type, formatIntegerDisplay]);

	const integerDisplayValue = isFocused ? rawIntegerValue : formatIntegerForInput(rawIntegerValue);

	return (
		<div className={cx(s.input__text, { [s.input__text_rtl]: dir === 'rtl' }, parentClassName)}>
			<input
				id={id && id}
				dir={dir}
				type={formatIntegerDisplay ? 'text' : type}
				inputMode={formatIntegerDisplay ? 'numeric' : undefined}
				{...(type === 'number' && !formatIntegerDisplay ? { min: 0 } : {})}
				placeholder={placeholder || ''}
				{...(!formatIntegerDisplay ? field : { name: field.name, onBlur: field.onBlur })}
				ref={ref}
				disabled={isDisabled}
				value={formatIntegerDisplay ? integerDisplayValue : isMultiple ? fieldValue : fieldValue || field.value}
				onChange={
					formatIntegerDisplay
						? event => syncIntegerValue(parseIntegerInput(event.target.value))
						: field.onChange
				}
				onFocus={
					formatIntegerDisplay
						? () => {
								setIsFocused(true);
						  }
						: undefined
				}
				onBlur={
					formatIntegerDisplay
						? event => {
								setIsFocused(false);
								field.onBlur(event);
								onBlur?.();
						  }
						: onBlur
				}
			/>
			{field && !!field.value && !isDisabled && <div className={s.input__text_edit} onClick={setFocus} />}
		</div>
	);
};

export default InputText;
